import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Key, Mail, Lock, Eye, EyeOff, MessageCircle, ShoppingCart, AlertTriangle, RefreshCw } from 'lucide-react';

const LoginForm = ({ isAdminLogin = null }) => {
  const navigate = useNavigate();
  const { loginWithKey, adminLogin, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(isAdminLogin === true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    accessKey: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [expiredAt, setExpiredAt] = useState(null);

  // Carregar credenciais salvas no localStorage E mensagem de expiração do sessionStorage
  useEffect(() => {
    // Primeiro, verificar se há uma mensagem de expiração no sessionStorage
    // Isso é usado para sobreviver a reloads causados pelo Service Worker
    const expiredInfo = sessionStorage.getItem('stakemind_login_expired');
    if (expiredInfo) {
      try {
        const { expired_at, email } = JSON.parse(expiredInfo);
        console.log('🔴 Recuperando estado de expiração do sessionStorage');
        setIsExpired(true);
        setExpiredAt(expired_at);
        setError('Sua assinatura expirou. Renove para continuar acessando.');
        
        // Preencher o email para facilitar
        if (email) {
          setFormData(prev => ({ ...prev, email }));
        }
        
        // Limpar o sessionStorage após usar
        sessionStorage.removeItem('stakemind_login_expired');
      } catch (error) {
        console.error('Erro ao carregar info de expiração:', error);
        sessionStorage.removeItem('stakemind_login_expired');
      }
    }
    
    const savedCredentials = localStorage.getItem('stakemind_remember_credentials');
    if (savedCredentials) {
      try {
        const { email, accessKey, password, type, remember } = JSON.parse(savedCredentials);
        setFormData({
          email: email || '',
          accessKey: accessKey || '',
          password: password || ''
        });
        setIsAdmin(type === 'admin');
        setRememberMe(remember || false);
        console.log('✅ Credenciais carregadas do localStorage');
      } catch (error) {
        console.error('Erro ao carregar credenciais salvas:', error);
        localStorage.removeItem('stakemind_remember_credentials');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsExpired(false);
    setExpiredAt(null);

    try {
      let result;
      if (isAdmin) {
        result = await adminLogin(formData.email, formData.password);
        const success = result === true; // adminLogin retorna boolean direto
        
        if (success && rememberMe) {
          // Salvar credenciais do admin se "lembrar-me" estiver marcado
          const credentialsToSave = {
            email: formData.email,
            password: formData.password,
            accessKey: '',
            type: 'admin',
            remember: true
          };
          localStorage.setItem('stakemind_remember_credentials', JSON.stringify(credentialsToSave));
          console.log('💾 Credenciais do admin salvas no localStorage');
        } else if (!success) {
          setError('Credenciais de administrador inválidas');
        }
      } else {
        result = await loginWithKey(formData.email, formData.accessKey);
        
        if (result.success && rememberMe) {
          // Salvar credenciais do usuário se "lembrar-me" estiver marcado
          const credentialsToSave = {
            email: formData.email,
            accessKey: formData.accessKey,
            password: '',
            type: 'user',
            remember: true
          };
          localStorage.setItem('stakemind_remember_credentials', JSON.stringify(credentialsToSave));
          console.log('💾 Credenciais do usuário salvas no localStorage');
        } else if (!result.success) {
          if (result.expired) {
            // Mostrar mensagem específica para chave expirada
            console.log('🔴 Setando isExpired = true');
            
            // Salvar no sessionStorage para sobreviver a reloads do Service Worker
            sessionStorage.setItem('stakemind_login_expired', JSON.stringify({
              expired_at: result.expired_at,
              email: formData.email,
              timestamp: new Date().toISOString()
            }));
            console.log('💾 Info de expiração salva no sessionStorage');
            
            setIsExpired(true);
            setExpiredAt(result.expired_at);
            setError(result.message || 'Sua assinatura expirou. Renove para continuar acessando.');
            console.log('🔴 Estado atualizado - isExpired:', true, 'expiredAt:', result.expired_at);
          } else {
            setIsExpired(false);
            setError(result.message || 'E-mail ou chave de acesso inválidos');
          }
        }
      }

      // Limpar credenciais salvas se "lembrar-me" não estiver marcado
      if (!rememberMe) {
        localStorage.removeItem('stakemind_remember_credentials');
      }

    } catch (error) {
      setError('Erro interno. Tente novamente.');
      console.error('Login error:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Se for admin login, retornar apenas o formulário sem wrapper
  if (isAdminLogin === true) {
    return (
      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              E-mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="admin@stakemind.com"
                className="w-full pl-10 pr-4 py-3 border border-slate-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-slate-700 text-white placeholder-gray-500"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Digite sua senha"
                className="w-full pl-10 pr-12 py-3 border border-slate-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-slate-700 text-white placeholder-gray-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Checkbox Lembrar-me */}
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-red-600 bg-slate-700 border-slate-600 rounded focus:ring-red-500 focus:ring-2"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm font-medium text-gray-300">
              Lembrar-me
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }
  
  // Login de usuário normal
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Header - Apenas para usuários */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden bg-white shadow-lg">
            <img 
              src="/stakemind-logo.jpeg" 
              alt="Stake Mind Logo" 
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Stake Mind
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sistema de Gestão de Apostas
          </p>
        </div>

        {/* Tab Switcher - Apenas se isAdminLogin não for definido (null) */}
        {isAdminLogin === null && (
          <div className="flex mb-6">
            <button
              type="button"
              onClick={() => setIsAdmin(false)}
              className={`flex-1 py-2 px-4 rounded-l-lg font-medium transition-colors ${
                !isAdmin
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              Usuário
            </button>
            <button
              type="button"
              onClick={() => setIsAdmin(true)}
              className={`flex-1 py-2 px-4 rounded-r-lg font-medium transition-colors ${
                isAdmin
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              Admin
            </button>
          </div>
        )}

        {/* Error Message - Mensagem de Expiração Especial */}
        {console.log('🔍 RENDER - isExpired:', isExpired, 'error:', error)}
        {isExpired ? (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                  Assinatura Expirada
                </h3>
                <p className="text-amber-600 dark:text-amber-300 text-sm mb-3">
                  Sua assinatura expirou{expiredAt ? ` em ${new Date(expiredAt).toLocaleDateString('pt-BR')}` : ''}. Renove agora para continuar tendo acesso completo ao sistema.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/checkout')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] shadow-lg"
                >
                  <RefreshCw className="w-5 h-5" />
                  Renovar Assinatura
                </button>
              </div>
            </div>
          </div>
        ) : error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              E-mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="seu@email.com"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Access Key or Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isAdmin ? 'Senha' : 'Chave de Acesso'}
            </label>
            <div className="relative">
              {isAdmin ? (
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              ) : (
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              )}
              <input
                type={isAdmin && !showPassword ? 'password' : 'text'}
                name={isAdmin ? 'password' : 'accessKey'}
                value={isAdmin ? formData.password : formData.accessKey}
                onChange={handleInputChange}
                required
                placeholder={isAdmin ? 'Digite sua senha' : 'STK-XXXXXXXX'}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Checkbox Lembrar-me */}
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Lembrar-me
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          {/* Comprar Licença - Only for users */}
          {!isAdmin && (
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Precisa de uma chave de acesso?
              </p>
              <a
                href="/checkout"
                className="inline-flex items-center space-x-2 px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Comprar Licença</span>
              </a>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © 2025 Stake Mind. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;